import './TechApp.css';
import { useState, useEffect } from 'react';
import { useParams } from "react-router-dom";
import FieldServiceAPI from '../Services/FieldService';

const EQUIPMENT_URL = "https://cornerstoneinc.io/EquipmentViewer/";

const formatDateForDateTimeLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const getAppointmentRecord = async (appointmentNumber, sessionId, setAppointmentRecord, setAppointmentRecordError) => {
    await FieldServiceAPI.getAppointmentBySession(sessionId, appointmentNumber).then((res) => {
        if(res.data){
            console.log(res.data)
            setAppointmentRecord(res.data);
        } else {
            setAppointmentRecord(null);
            setAppointmentRecordError("No record found");
        }
    }).catch((err) => {
        console.log(err);
        setAppointmentRecord(null);
        setAppointmentRecordError("This link is invalid or has expired.");
    });
};

const updateAppointmentStatus = async (token, appointmentNbr, newStatus) => {
    try {
        const response = await FieldServiceAPI.updateAppointmentStatus(token, appointmentNbr, newStatus);
        if (response.data) {
            console.log("Appointment status updated successfully:", response.data);
            return true;
        }

        console.log("Failed to update appointment status. No data returned.");
        return false;
    } catch (error) {
        console.error("Error updating appointment status:", error);
        return false;
    }
};

const getPreviousWork = async (sessionId, customerId, locationId, setPreviousWork, setPreviousWorkError) => {
    await FieldServiceAPI.getPreviousWorkBySession(sessionId, customerId, locationId).then((res) => {
        if(res.data){
            console.log("Previous work data:", res.data);
            setPreviousWork(res.data);
        } else {
            setPreviousWork([]);
            setPreviousWorkError("No previous work found");
        }
    }).catch((err) => {
        console.log(err);
        setPreviousWork([]);
        setPreviousWorkError("This link is invalid or has expired.");
    });
};

const submitAppointmentCompletion = async (completionPayload) => {
    try {
        const response = await FieldServiceAPI.completeAppointment(completionPayload);
        return response.data;
    } catch (error) {
        console.error("Error submitting appointment completion:", error);
        throw error;
    }
};

const getAppointmentStartTimeCacheKey = (appointmentNumber, sessionId) => `appointmentStartTime_${appointmentNumber}_${sessionId}`;

const getCompletionCacheKey = (appointmentNumber, sessionId) => `completionData_${appointmentNumber}_${sessionId}`;

const getCurrentLocalDateTime = () => formatDateForDateTimeLocal(new Date());

const getAllAssociatedHelpers = async (sessionId) => {
    try {
        const response = await FieldServiceAPI.getAllAssociatedHelpers(sessionId);
        if(response.data){
            console.log("Associated helpers data:", response.data);
            return response.data;
        } else {
            console.log("No associated helpers found for this session.");
            return null;
        }
    } catch (error) {
        console.error("Error fetching associated helpers:", error);
        return null;
    };
};

const TechApp = () => {
    const { appointmentNumber, sessionId } = useParams();

    const [appointmentRecord, setAppointmentRecord] = useState(null);
    const [appointmentRecordError, setAppointmentRecordError] = useState(null);
    const [previousWork, setPreviousWork] = useState([]);
    const [previousWorkError, setPreviousWorkError] = useState(null);
    const [expandedPreviousWork, setExpandedPreviousWork] = useState({});
    const [showCompletionForm, setShowCompletionForm] = useState(false);
    const [showInternalNotes, setShowInternalNotes] = useState(true);
    const [internalNotesPin, setInternalNotesPin] = useState('');
    const [isPinProtected, setIsPinProtected] = useState(false);
    const [showPinModal, setShowPinModal] = useState(false);
    const [pinModalType, setPinModalType] = useState('');
    const [currentPin, setCurrentPin] = useState('');
    const [pinError, setPinError] = useState(false);
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);
    const [submissionError, setSubmissionError] = useState(null);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [completionData, setCompletionData] = useState({
        actualStartTime: '',
        actualEndTime: '',
        travelHours: '',
        travelMinutes: '',
        onsiteHours: '',
        onsiteMinutes: '',
        notes: '',
        safetyMeeting: false,
        oacMeeting: false,
        signature: '',
        techSignature: '',
        troubleDescription: '',
        repairsDescription: '',
        additionalComments: '',
        associatedHelp: [],
        attachments: [],
        completionPayload: null
    });

    const [allAssociatedHelpers, setAllAssociatedHelpers] = useState({
        internalTechs: [],
        externalTechs: []
    });
    const [associatedHelpInput, setAssociatedHelpInput] = useState('');
    const [filteredAssociatedHelpers, setFilteredAssociatedHelpers] = useState({
        internalTechs: [],
        externalTechs: []
    });
    const [showAssociatedHelpDropdown, setShowAssociatedHelpDropdown] = useState(false);
    const [showCreateExternalHelp, setShowCreateExternalHelp] = useState(false);
    const [newExternalHelp, setNewExternalHelp] = useState({
        company: '',
        name: '',
        phone: '',
        email: ''
    });
    
    // Add signature modal states
    const [showSignatureModal, setShowSignatureModal] = useState(false);
    const [signatureData, setSignatureData] = useState({
        printedName: '',
        signatureBase64: ''
    });
    const [signatureCanvas, setSignatureCanvas] = useState(null);

    // Add technician signature modal states
    const [showTechSignatureModal, setShowTechSignatureModal] = useState(false);
    const [techSignatureData, setTechSignatureData] = useState({
        printedName: '',
        signatureBase64: ''
    });
    const [techSignatureCanvas, setTechSignatureCanvas] = useState(null);

    const isInstAppointment = appointmentRecord?.type === 'INST';
    const statusActionLabel = appointmentRecord?.status == null
        ? 'Confirm Appointment'
        : appointmentRecord?.status === 'Confirmed'
            ? 'Enroute'
            : appointmentRecord?.status === 'Enroute'
                ? 'Onsite'
                : null;

    const handleUpdateStatus = async () => {
        if (!appointmentRecord || !sessionId || !appointmentNumber || !statusActionLabel || isUpdatingStatus) return;

        const nextStatus = appointmentRecord.status == null
            ? 'Confirmed'
            : appointmentRecord.status === 'Confirmed'
                ? 'Enroute'
                : appointmentRecord.status === 'Enroute'
                    ? 'Onsite'
                    : null;

        if (!nextStatus) {
            return;
        }

        setIsUpdatingStatus(true);
        setSubmissionError(null);

        try {
            const updated = await updateAppointmentStatus(sessionId, appointmentNumber, nextStatus);

            if (!updated) {
                setSubmissionError('Unable to update appointment status at this time.');
                return;
            }

            setAppointmentRecord(prev => prev ? { ...prev, status: nextStatus } : prev);

            if (nextStatus === 'Onsite') {
                const startedAt = getCurrentLocalDateTime();
                localStorage.setItem(getAppointmentStartTimeCacheKey(appointmentNumber, sessionId), startedAt);
            }
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    useEffect(() => {
        if(sessionId && appointmentNumber) getAppointmentRecord(appointmentNumber, sessionId, setAppointmentRecord, setAppointmentRecordError);
    },[]);

    useEffect(() => {
        console.log(appointmentRecord)
        if(appointmentRecord && previousWork.length < 1){
            getPreviousWork(sessionId, appointmentRecord.customer?.id, appointmentRecord.location?.locationID, setPreviousWork, setPreviousWorkError);
        };
    },[appointmentRecord]);

    // Cache completionData to localStorage whenever it changes
    useEffect(() => {
        console.log("Caching completion data:", completionData);
        if (showCompletionForm && completionData.completionPayload) {
            const cacheKey = getCompletionCacheKey(appointmentNumber, sessionId);
            const cacheSafeCompletionData = {
                ...completionData,
                attachments: [],
                completionPayload: completionData.completionPayload ? {
                    ...completionData.completionPayload,
                    attachments: []
                } : null
            };
            localStorage.setItem(cacheKey, JSON.stringify(cacheSafeCompletionData));
        }
    }, [completionData, showCompletionForm, appointmentNumber, sessionId]);

    useEffect(() => {
        if (!showCompletionForm || !sessionId) return;

        const loadAssociatedHelpers = async () => {
            const helperData = await getAllAssociatedHelpers(sessionId);
            if (helperData) {
                setAllAssociatedHelpers({
                    internalTechs: helperData.internalTechs || [],
                    externalTechs: helperData.externalTechs || []
                });
            }
        };

        loadAssociatedHelpers();
    }, [showCompletionForm, sessionId]);

    useEffect(() => {
        const searchTerm = associatedHelpInput.trim().toLowerCase();

        if (!searchTerm || showCreateExternalHelp) {
            setFilteredAssociatedHelpers({ internalTechs: [], externalTechs: [] });
            setShowAssociatedHelpDropdown(false);
            return;
        }

        const selectedKeys = new Set((completionData.associatedHelp || []).map((helper) => `${helper.type || ''}-${helper.id || helper.email || helper.name}`));

        const helperMatchesSearch = (helper) => {
            const fields = [helper.name, helper.company, helper.email, helper.phone].filter(Boolean).map((field) => String(field).toLowerCase());
            return fields.some((field) => field.includes(searchTerm));
        };

        const internalMatches = (allAssociatedHelpers.internalTechs || [])
            .filter(helperMatchesSearch)
            .filter((helper) => !selectedKeys.has(`${helper.type || 'internal'}-${helper.id || helper.email || helper.name}`));

        const externalMatches = (allAssociatedHelpers.externalTechs || [])
            .filter(helperMatchesSearch)
            .filter((helper) => !selectedKeys.has(`${helper.type || 'external'}-${helper.id || helper.email || helper.name}`));

        setFilteredAssociatedHelpers({
            internalTechs: internalMatches,
            externalTechs: externalMatches
        });
        setShowAssociatedHelpDropdown(true);
    }, [associatedHelpInput, allAssociatedHelpers, completionData.associatedHelp, showCreateExternalHelp]);


    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return 'Not specified';
        
        // Handle the datetime string format (e.g., "2026-02-05 09:00:00")
        // Replace space with T for proper Date parsing
        const isoDateTime = dateTimeString.replace(' ', 'T');
        const date = new Date(isoDateTime);
        
        // Format the date and time
        return date.toLocaleDateString() + ' at ' + date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
    };

    const formatAppointmentDate = (dateString) => {
        if (!dateString) return 'Date not specified';
        const date = new Date(dateString);
        return date.toLocaleDateString();
    };

    const togglePreviousWorkExpanded = (appointmentId) => {
        setExpandedPreviousWork(prev => ({
            ...prev,
            [appointmentId]: !prev[appointmentId]
        }));
    };

    // Function to check if all required fields are completed
    const isFormReadyForSubmission = () => {
        // Check if travel duration is entered (either hours or minutes must have value)
        const hasTravelDuration = (completionData.travelHours && completionData.travelHours !== '') || 
                                 (completionData.travelMinutes && completionData.travelMinutes !== '');
        
        // Check if onsite duration is entered (either hours or minutes must have value)
        const hasOnsiteDuration = (completionData.onsiteHours && completionData.onsiteHours !== '') || 
                                 (completionData.onsiteMinutes && completionData.onsiteMinutes !== '');
        
        // Check if trouble description is entered
        const hasTroubleDescription = isInstAppointment || (completionData.troubleDescription && completionData.troubleDescription.trim() !== '');
        
        // Check if repairs description is entered
        const hasRepairsDescription = isInstAppointment || (completionData.repairsDescription && completionData.repairsDescription.trim() !== '');
        
        // Check if customer signature is captured
        const hasCustomerSignature = isInstAppointment || (completionData.signature && completionData.signature !== '');
        
        // Check if technician signature is captured
        const hasTechnicianSignature = completionData.techSignature && completionData.techSignature !== '';

        // Daily Journal is required for INST appointments
        const hasDailyJournal = !isInstAppointment || (completionData.additionalComments && completionData.additionalComments.trim() !== '');
        
        return hasTravelDuration && hasOnsiteDuration && hasTroubleDescription && hasRepairsDescription && hasCustomerSignature && hasTechnicianSignature && hasDailyJournal;
    };

    const handleManageEquipment = () => {
        if (appointmentRecord && appointmentRecord.customer?.id && appointmentRecord.location?.locationID) {
            const equipmentUrl = `${EQUIPMENT_URL}${sessionId}/${appointmentRecord.customer.id}/${appointmentRecord.location.locationID}`;
            window.open(equipmentUrl, '_blank');
        }
    };

    const handleCompleteAppointment = () => {
        // Check for cached data first
        const cacheKey = getCompletionCacheKey(appointmentNumber, sessionId);
        const cachedData = localStorage.getItem(cacheKey);
        
        if (cachedData) {
            try {
                const parsedCache = JSON.parse(cachedData);
                setCompletionData(prev => ({
                    ...prev,
                    ...parsedCache,
                    safetyMeeting: parsedCache.safetyMeeting ?? false,
                    oacMeeting: parsedCache.oacMeeting ?? false,
                    associatedHelp: parsedCache.associatedHelp || [],
                    attachments: []
                }));
                setShowCompletionForm(true);
                return;
            } catch (error) {
                console.error('Error parsing cached completion data:', error);
                // Continue with fresh data if cache is corrupted
            }
        }
        
        // Set current date and time for actual end time (local time)
        const currentDateTime = getCurrentLocalDateTime();
        
        // Prefer a cached start time captured when the appointment was moved to In Progress
        let scheduledStartTime = '';

        const cachedStartTime = localStorage.getItem(getAppointmentStartTimeCacheKey(appointmentNumber, sessionId));
        if (cachedStartTime) {
            scheduledStartTime = cachedStartTime;
        } else if (appointmentRecord.scheduledStart) {
            // Handle the datetime string format (e.g., "2026-02-05 09:00:00")
            const isoDateTime = appointmentRecord.scheduledStart.replace(' ', 'T');
            const scheduledDate = new Date(isoDateTime);

            scheduledStartTime = formatDateForDateTimeLocal(scheduledDate);
        }
        
        // Create completion payload object for backend
        const completionPayload = {
            appointmentID: appointmentRecord.appointmentID,
            sessionId: sessionId,
            appointmentNumber: appointmentNumber,
            customerID: appointmentRecord.customer?.id,
            actualStartTime: scheduledStartTime,
            actualEndTime: currentDateTime,
            travelHours: '',
            travelMinutes: '',
            onsiteHours: '',
            onsiteMinutes: '',
            notes: '',
            safetyMeeting: false,
            oacMeeting: false,
            signature: '',
            troubleDescription: '',
            repairsDescription: '',
            additionalComments: '',
            associatedHelp: [],
            attachments: [],
            completedDate: new Date().toISOString(),
            status: 'completed'
        };
        
        setCompletionData(prev => ({
            ...prev,
            actualStartTime: scheduledStartTime,
            actualEndTime: currentDateTime,
            completionPayload: completionPayload
        }));
        
        setShowCompletionForm(true);
    };

    const handleCompletionSubmit = async (e) => {
        e.preventDefault();
        
        // Clear any previous error and start loading
        setSubmissionError(null);
        setIsSubmittingForm(true);
        
        // Create final payload with all form data
        const finalPayload = {
            ...completionData.completionPayload,
            actualStartTime: completionData.actualStartTime,
            actualEndTime: completionData.actualEndTime,
            travelHours: completionData.travelHours,
            travelMinutes: completionData.travelMinutes,
            onsiteHours: completionData.onsiteHours,
            onsiteMinutes: completionData.onsiteMinutes,
            notes: completionData.notes,
            safetyMeeting: !!completionData.safetyMeeting,
            oacMeeting: !!completionData.oacMeeting,
            signature: completionData.signature,
            customerName: completionData.customerName,
            techSignature: completionData.techSignature,
            technicianName: completionData.technicianName,
            troubleDescription: completionData.troubleDescription,
            repairsDescription: completionData.repairsDescription,
            additionalComments: completionData.additionalComments,
            associatedHelp: completionData.associatedHelp || [],
            attachments: completionData.attachments || [],
            siteName: appointmentRecord.location?.locationName || '',
            siteAddress: appointmentRecord.location?.address || {},
            
            // Calculate total travel time in minutes
            totalTravelMinutes: (parseInt(completionData.travelHours) || 0) * 60 + (parseInt(completionData.travelMinutes) || 0),
            // Calculate total onsite time in minutes
            totalOnsiteMinutes: (parseInt(completionData.onsiteHours) || 0) * 60 + (parseInt(completionData.onsiteMinutes) || 0),
            submittedAt: new Date().toISOString()
        };
        
        console.log('Final completion payload for backend:', finalPayload);
        
        try {
            // Send finalPayload to backend API
            await submitAppointmentCompletion(finalPayload);
            
            // Clear cached data on successful submission
            const cacheKey = getCompletionCacheKey(appointmentNumber, sessionId);
            localStorage.removeItem(cacheKey);
            localStorage.removeItem(getAppointmentStartTimeCacheKey(appointmentNumber, sessionId));
            
            alert('Appointment completed successfully!');
            setIsSubmittingForm(false);
            // Redirect to Cornerstone website on success
            window.location.href = 'https://cornerstoneinc.io';
        } catch (error) {
            console.error('Error completing appointment:', error);
            setSubmissionError("Failed to submit the FSR try again when service is available");
            setIsSubmittingForm(false);
            // Don't clear form fields on error - preserve user's input
        }
    };

    const handleInputChange = (field, value) => {
        // Clear any submission error when user starts editing
        if (submissionError) {
            setSubmissionError(null);
        }
        
        // Validate minutes fields to enforce 0-59 range
        if (field === 'travelMinutes' || field === 'onsiteMinutes') {
            const numValue = parseInt(value);
            if (value !== '' && (!isNaN(numValue))) {
                if (numValue < 0) {
                    value = '0';
                } else if (numValue > 59) {
                    value = '59';
                }
            }
        }
        
        // Allow hours to be any value 0 or greater (no upper limit)
        if (field === 'travelHours' || field === 'onsiteHours') {
            const numValue = parseInt(value);
            if (value !== '' && (!isNaN(numValue))) {
                if (numValue < 0) {
                    value = '0';
                }
                // Removed max validation for hours
            }
        }
        
        setCompletionData(prev => ({
            ...prev,
            [field]: value,
            completionPayload: prev.completionPayload ? {
                ...prev.completionPayload,
                [field]: value
            } : null
        }));
    };

    const updateAssociatedHelp = (nextAssociatedHelp) => {
        setCompletionData(prev => ({
            ...prev,
            associatedHelp: nextAssociatedHelp,
            completionPayload: prev.completionPayload ? {
                ...prev.completionPayload,
                associatedHelp: nextAssociatedHelp
            } : null
        }));
    };

    const updateAttachments = (nextAttachments) => {
        setCompletionData(prev => ({
            ...prev,
            attachments: nextAttachments,
            completionPayload: prev.completionPayload ? {
                ...prev.completionPayload,
                attachments: nextAttachments
            } : null
        }));
    };

    const fileToAttachmentObject = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const result = reader.result || '';
                const base64Content = typeof result === 'string' && result.includes(',') ? result.split(',')[1] : '';

                resolve({
                    fileName: file.name,
                    mimeType: file.type || 'application/octet-stream',
                    size: file.size,
                    lastModified: file.lastModified,
                    contentBase64: base64Content
                });
            };
            reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
            reader.readAsDataURL(file);
        });
    };

    const handleAttachmentFilesSelected = async (event) => {
        const files = Array.from(event.target.files || []);
        if (files.length < 1) return;

        try {
            const mappedAttachments = await Promise.all(files.map(fileToAttachmentObject));
            const existingAttachments = completionData.attachments || [];
            const existingKeys = new Set(existingAttachments.map((attachment) => `${attachment.fileName}-${attachment.size}-${attachment.lastModified}`));

            const dedupedNewAttachments = mappedAttachments.filter((attachment) => {
                const key = `${attachment.fileName}-${attachment.size}-${attachment.lastModified}`;
                return !existingKeys.has(key);
            });

            updateAttachments([...existingAttachments, ...dedupedNewAttachments]);
        } catch (error) {
            console.error('Error reading attachment files:', error);
            setSubmissionError('Unable to process one or more attachments. Please try again.');
        } finally {
            event.target.value = '';
        }
    };

    const handleRemoveAttachment = (attachmentToRemove) => {
        const nextAttachments = (completionData.attachments || []).filter((attachment) => {
            const attachmentKey = `${attachment.fileName}-${attachment.size}-${attachment.lastModified}`;
            const removeKey = `${attachmentToRemove.fileName}-${attachmentToRemove.size}-${attachmentToRemove.lastModified}`;
            return attachmentKey !== removeKey;
        });

        updateAttachments(nextAttachments);
    };

    const handleSelectAssociatedHelper = (helper) => {
        const nextAssociatedHelp = [...(completionData.associatedHelp || []), helper];
        updateAssociatedHelp(nextAssociatedHelp);
        setAssociatedHelpInput('');
        setShowAssociatedHelpDropdown(false);
        setShowCreateExternalHelp(false);
    };

    const handleRemoveAssociatedHelper = (helperToRemove) => {
        const nextAssociatedHelp = (completionData.associatedHelp || []).filter((helper) => {
            const helperKey = `${helper.type || ''}-${helper.id || helper.email || helper.name}`;
            const removeKey = `${helperToRemove.type || ''}-${helperToRemove.id || helperToRemove.email || helperToRemove.name}`;
            return helperKey !== removeKey;
        });

        updateAssociatedHelp(nextAssociatedHelp);
    };

    const handleStartCreateExternalHelp = () => {
        setShowCreateExternalHelp(true);
        setShowAssociatedHelpDropdown(false);
        setNewExternalHelp({
            company: '',
            name: associatedHelpInput.trim(),
            phone: '',
            email: ''
        });
    };

    const canCreateExternalHelp = () => {
        const hasName = newExternalHelp.name && newExternalHelp.name.trim() !== '';
        const trimmedPhone = (newExternalHelp.phone || '').trim();
        const trimmedEmail = (newExternalHelp.email || '').trim();

        const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        const isValidPhone = (phone) => {
            const digits = phone.replace(/\D/g, '');
            return digits.length >= 10 && digits.length <= 15;
        };

        const hasValidPhone = trimmedPhone !== '' && isValidPhone(trimmedPhone);
        const hasValidEmail = trimmedEmail !== '' && isValidEmail(trimmedEmail);
        const hasAtLeastOneValidContact = hasValidPhone || hasValidEmail;

        const hasInvalidProvidedContact = (trimmedPhone !== '' && !isValidPhone(trimmedPhone)) || (trimmedEmail !== '' && !isValidEmail(trimmedEmail));

        return hasName && hasAtLeastOneValidContact && !hasInvalidProvidedContact;
    };

    const getExternalHelpMissingRequirements = () => {
        const missingRequirements = [];
        const trimmedPhone = (newExternalHelp.phone || '').trim();
        const trimmedEmail = (newExternalHelp.email || '').trim();

        const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
        const isValidPhone = (phone) => {
            const digits = phone.replace(/\D/g, '');
            return digits.length >= 10 && digits.length <= 15;
        };

        if (!newExternalHelp.name || newExternalHelp.name.trim() === '') {
            missingRequirements.push('Name is required.');
        }

        if (trimmedPhone === '' && trimmedEmail === '') {
            missingRequirements.push('At least one contact method is required: phone or email.');
        }

        if (trimmedPhone !== '' && !isValidPhone(trimmedPhone)) {
            missingRequirements.push('Phone must be a valid number with 10 to 15 digits.');
        }

        if (trimmedEmail !== '' && !isValidEmail(trimmedEmail)) {
            missingRequirements.push('Email must be a valid email address.');
        }

        return missingRequirements;
    };

    const handleAddExternalHelp = () => {
        if (!canCreateExternalHelp()) return;

        const createdBy = appointmentRecord?.technicians?.[0]?.email || '';
        const newHelper = {
            company: newExternalHelp.company?.trim() || '',
            name: newExternalHelp.name.trim(),
            phone: newExternalHelp.phone?.trim() || '',
            email: newExternalHelp.email?.trim() || '',
            createdBy,
            type: 'external'
        };

        const nextAssociatedHelp = [...(completionData.associatedHelp || []), newHelper];
        updateAssociatedHelp(nextAssociatedHelp);

        setShowCreateExternalHelp(false);
        setAssociatedHelpInput('');
        setNewExternalHelp({ company: '', name: '', phone: '', email: '' });
    };

    const getAssociatedHelperLabel = (helper) => {
        const details = [];
        if (helper.company) details.push(helper.company);
        if (helper.email) details.push(helper.email);
        if (helper.phone) details.push(helper.phone);

        return details.length > 0 ? `${helper.name} (${details.join(' | ')})` : helper.name;
    };

    const handleToggleInternalNotes = () => {
        if (showInternalNotes) {
            // Hiding notes - request PIN
            setPinModalType('set');
            setCurrentPin('');
            setShowPinModal(true);
        } else {
            // Showing notes - verify PIN
            if (isPinProtected) {
                setPinModalType('verify');
                setCurrentPin('');
                setShowPinModal(true);
            } else {
                setShowInternalNotes(true);
            }
        }
    };

    const handlePinDigit = (digit) => {
        // Clear error state when user starts typing
        if (pinError) {
            setPinError(false);
        }
        
        if (currentPin.length < 4) {
            const newPin = currentPin + digit;
            setCurrentPin(newPin);
            
            // Auto-submit when 4 digits are entered
            if (newPin.length === 4) {
                setTimeout(() => {
                    if (pinModalType === 'set') {
                        setInternalNotesPin(newPin);
                        setShowInternalNotes(false);
                        setIsPinProtected(true);
                        setShowPinModal(false);
                        setCurrentPin('');
                    } else if (pinModalType === 'verify') {
                        if (newPin === internalNotesPin) {
                            setShowInternalNotes(true);
                            setShowPinModal(false);
                            setCurrentPin('');
                            setPinError(false);
                        } else {
                            setPinError(true);
                            setCurrentPin('');
                        }
                    }
                }, 100);
            }
        }
    };

    const handlePinClear = () => {
        setCurrentPin('');
        setPinError(false);
    };

    const handlePinCancel = () => {
        setShowPinModal(false);
        setCurrentPin('');
        setPinError(false);
    };

    // Signature modal handlers
    const handleOpenSignatureModal = () => {
        setShowSignatureModal(true);
        setSignatureData({ printedName: '', signatureBase64: '' });
    };

    const handleCloseSignatureModal = () => {
        // Clean up canvas event listeners
        if (signatureCanvas && signatureCanvas.cleanup) {
            signatureCanvas.cleanup();
        }
        setShowSignatureModal(false);
        setSignatureData({ printedName: '', signatureBase64: '' });
    };

    const handleClearSignature = () => {
        if (signatureCanvas) {
            const canvas = signatureCanvas;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setSignatureData(prev => ({ ...prev, signatureBase64: '' }));
        }
    };

    const handleSubmitSignature = () => {
        if (signatureCanvas && signatureData.printedName.trim()) {
            const signatureBase64 = signatureCanvas.toDataURL();
            setCompletionData(prev => ({
                ...prev,
                signature: signatureBase64,
                customerName: signatureData.printedName
            }));
            // Clean up canvas event listeners
            if (signatureCanvas.cleanup) {
                signatureCanvas.cleanup();
            }
            setShowSignatureModal(false);
        }
    };

    // Technician signature modal handlers
    const handleOpenTechSignatureModal = () => {
        setShowTechSignatureModal(true);
        setTechSignatureData({ printedName: '', signatureBase64: '' });
    };

    const handleCloseTechSignatureModal = () => {
        // Clean up canvas event listeners
        if (techSignatureCanvas && techSignatureCanvas.cleanup) {
            techSignatureCanvas.cleanup();
        }
        setShowTechSignatureModal(false);
        setTechSignatureData({ printedName: '', signatureBase64: '' });
    };

    const handleClearTechSignature = () => {
        if (techSignatureCanvas) {
            const canvas = techSignatureCanvas;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setTechSignatureData(prev => ({ ...prev, signatureBase64: '' }));
        }
    };

    const handleSubmitTechSignature = () => {
        if (techSignatureCanvas && techSignatureData.printedName.trim()) {
            const signatureBase64 = techSignatureCanvas.toDataURL();
            setCompletionData(prev => ({
                ...prev,
                techSignature: signatureBase64,
                technicianName: techSignatureData.printedName
            }));
            // Clean up canvas event listeners
            if (techSignatureCanvas.cleanup) {
                techSignatureCanvas.cleanup();
            }
            setShowTechSignatureModal(false);
        }
    };

    const initializeTechCanvas = (canvas) => {
        if (!canvas) return;
        
        setTechSignatureCanvas(canvas);
        const ctx = canvas.getContext('2d');
        
        // Function to resize canvas and maintain aspect ratio
        const resizeCanvas = () => {
            // Save existing drawing if any
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            
            // Set canvas size with proper aspect ratio for signatures (3:2 ratio, width always >= height)
            const maxWidth = window.innerWidth;
            const maxHeight = Math.min(window.innerHeight * 0.6, maxWidth * 0.67); // Ensure height <= width
            canvas.width = maxWidth;
            canvas.height = Math.min(maxHeight, maxWidth * 0.67); // 3:2 aspect ratio maximum
            
            // Restore drawing properties
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            
            // Restore existing drawing
            ctx.putImageData(imageData, 0, 0);
        };
        
        // Initial canvas setup
        resizeCanvas();
        
        let isDrawing = false;
        let lastX = 0;
        let lastY = 0;

        const getCanvasCoordinates = (e) => {
            const rect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;
            
            const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
            const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
            
            return {
                x: (clientX - rect.left) * scaleX,
                y: (clientY - rect.top) * scaleY
            };
        };

        const startDrawing = (e) => {
            isDrawing = true;
            const coords = getCanvasCoordinates(e);
            [lastX, lastY] = [coords.x, coords.y];
        };

        const draw = (e) => {
            if (!isDrawing) return;
            
            const coords = getCanvasCoordinates(e);
            
            ctx.beginPath();
            ctx.moveTo(lastX, lastY);
            ctx.lineTo(coords.x, coords.y);
            ctx.stroke();
            
            [lastX, lastY] = [coords.x, coords.y];
        };

        const stopDrawing = () => {
            isDrawing = false;
        };

        // Orientation change handler
        const handleOrientationChange = () => {
            // Small delay to ensure viewport has updated
            setTimeout(resizeCanvas, 100);
        };

        // Mouse events
        canvas.addEventListener('mousedown', startDrawing);
        canvas.addEventListener('mousemove', draw);
        canvas.addEventListener('mouseup', stopDrawing);
        canvas.addEventListener('mouseout', stopDrawing);

        // Touch events
        canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            startDrawing(e);
        });
        canvas.addEventListener('touchmove', (e) => {
            e.preventDefault();
            draw(e);
        });
        canvas.addEventListener('touchend', (e) => {
            e.preventDefault();
            stopDrawing();
        });

        // Listen for orientation and resize events
        window.addEventListener('orientationchange', handleOrientationChange);
        window.addEventListener('resize', handleOrientationChange);

        // Cleanup function to remove event listeners
        const cleanup = () => {
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.removeEventListener('resize', handleOrientationChange);
        };

        // Store cleanup function for later use
        canvas.cleanup = cleanup;
    };

    const initializeCanvas = (canvas) => {
        if (!canvas) return;
        
        setSignatureCanvas(canvas);
        const ctx = canvas.getContext('2d');
        
        // Function to resize canvas and maintain aspect ratio
        const resizeCanvas = () => {
            // Save existing drawing if any
            const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
            
            // Set canvas size with proper aspect ratio for signatures (3:2 ratio, width always >= height)
            const maxWidth = window.innerWidth;
            const maxHeight = Math.min(window.innerHeight * 0.6, maxWidth * 0.67); // Ensure height <= width
            canvas.width = maxWidth;
            canvas.height = Math.min(maxHeight, maxWidth * 0.67); // 3:2 aspect ratio maximum
            
            // Restore drawing properties
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 3;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            
            // Restore existing drawing
            ctx.putImageData(imageData, 0, 0);
        };
        
        // Initial canvas setup
        resizeCanvas();
        
        let isDrawing = false;
        let lastX = 0;
        let lastY = 0;

        const getCanvasCoordinates = (e) => {
            const rect = canvas.getBoundingClientRect();
            const scaleX = canvas.width / rect.width;
            const scaleY = canvas.height / rect.height;
            
            const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
            const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
            
            return {
                x: (clientX - rect.left) * scaleX,
                y: (clientY - rect.top) * scaleY
            };
        };

        const startDrawing = (e) => {
            isDrawing = true;
            const coords = getCanvasCoordinates(e);
            [lastX, lastY] = [coords.x, coords.y];
        };

        const draw = (e) => {
            if (!isDrawing) return;
            
            const coords = getCanvasCoordinates(e);
            
            ctx.beginPath();
            ctx.moveTo(lastX, lastY);
            ctx.lineTo(coords.x, coords.y);
            ctx.stroke();
            
            [lastX, lastY] = [coords.x, coords.y];
        };

        const stopDrawing = () => {
            isDrawing = false;
        };

        // Orientation change handler
        const handleOrientationChange = () => {
            // Small delay to ensure viewport has updated
            setTimeout(resizeCanvas, 100);
        };

        // Mouse events
        canvas.addEventListener('mousedown', startDrawing);
        canvas.addEventListener('mousemove', draw);
        canvas.addEventListener('mouseup', stopDrawing);
        canvas.addEventListener('mouseout', stopDrawing);

        // Touch events
        canvas.addEventListener('touchstart', (e) => {
            e.preventDefault();
            startDrawing(e);
        });
        canvas.addEventListener('touchmove', (e) => {
            e.preventDefault();
            draw(e);
        });
        canvas.addEventListener('touchend', (e) => {
            e.preventDefault();
            stopDrawing();
        });

        // Listen for orientation and resize events
        window.addEventListener('orientationchange', handleOrientationChange);
        window.addEventListener('resize', handleOrientationChange);

        // Cleanup function to remove event listeners
        const cleanup = () => {
            window.removeEventListener('orientationchange', handleOrientationChange);
            window.removeEventListener('resize', handleOrientationChange);
        };

        // Store cleanup function for later use
        canvas.cleanup = cleanup;
    };

    if (appointmentRecordError) {
        return (
            <div className="TechApp">
                <div className="error-message">
                    <h2>Error</h2>
                    <p>{appointmentRecordError}</p>
                </div>
            </div>
        );
    }

    if (!appointmentRecord) {
        return (
            <div className="TechApp">
                <div className="loading">
                    <h2>Loading appointment...</h2>
                </div>
            </div>
        );
    }

    if (showCompletionForm) {
        return (
            <div className="TechApp">
                {showPinModal && (
                    <div className="pin-modal">
                        <div className="modal-content">
                            <h2>{pinModalType === 'set' ? 'Set PIN' : 'Verify PIN'}</h2>
                            <div className={`pin-display ${pinError ? 'pin-error' : ''}`}>
                                {pinError ? 'WRONG' : currentPin.padEnd(4, '*')}
                            </div>
                            <div className="keypad">
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 0].map((digit) => (
                                    <button key={digit} onClick={() => handlePinDigit(digit)}>
                                        {digit}
                                    </button>
                                ))}
                                <button onClick={handlePinClear}>Clear</button>
                                <button onClick={handlePinCancel}>Cancel</button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Signature Modal */}
                {showSignatureModal && (
                    <div className="signature-modal">
                        <div className="signature-modal-content">
                            <div className="signature-header">
                                <h2>Customer Signature</h2>
                            </div>
                            
                            <div className="signature-form">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        id="printedName"
                                        value={signatureData.printedName}
                                        onChange={(e) => setSignatureData(prev => ({ 
                                            ...prev, 
                                            printedName: e.target.value 
                                        }))}
                                        placeholder="Enter customer's printed name"
                                        required
                                    />
                                </div>
                                
                                <div className="signature-area">
                                    <div className="signature-instructions">
                                        Please sign in the area below
                                    </div>
                                    <canvas
                                        ref={initializeCanvas}
                                        className="signature-canvas"
                                    />
                                </div>
                                
                                <div className="signature-actions">
                                    <button 
                                        type="button" 
                                        className="btn-clear-signature" 
                                        onClick={handleClearSignature}
                                    >
                                        Clear Signature
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn-cancel-signature" 
                                        onClick={handleCloseSignatureModal}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn-submit-signature" 
                                        onClick={handleSubmitSignature}
                                        disabled={!signatureData.printedName.trim()}
                                    >
                                        Submit Signature
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Technician Signature Modal */}
                {showTechSignatureModal && (
                    <div className="signature-modal">
                        <div className="signature-modal-content">
                            <div className="signature-header">
                                <h2>Technician Signature</h2>
                            </div>
                            
                            <div className="signature-form">
                                <div className="form-group">
                                    <input
                                        type="text"
                                        id="techPrintedName"
                                        value={techSignatureData.printedName}
                                        onChange={(e) => setTechSignatureData(prev => ({ 
                                            ...prev, 
                                            printedName: e.target.value 
                                        }))}
                                        placeholder="Enter technician's printed name"
                                        required
                                    />
                                </div>
                                
                                <div className="signature-area">
                                    <div className="signature-instructions">
                                        Please sign in the area below
                                    </div>
                                    <canvas
                                        ref={initializeTechCanvas}
                                        className="signature-canvas"
                                    />
                                </div>
                                
                                <div className="signature-actions">
                                    <button 
                                        type="button" 
                                        className="btn-clear-signature" 
                                        onClick={handleClearTechSignature}
                                    >
                                        Clear Signature
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn-cancel-signature" 
                                        onClick={handleCloseTechSignatureModal}
                                    >
                                        Cancel
                                    </button>
                                    <button 
                                        type="button" 
                                        className="btn-submit-signature" 
                                        onClick={handleSubmitTechSignature}
                                        disabled={!techSignatureData.printedName.trim()}
                                    >
                                        Submit Signature
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="completion-form">
                    <h2>Complete Appointment</h2>
                    <p className="appointment-id">ID: {appointmentRecord.appointmentID}</p>
                    
                    <form onSubmit={handleCompletionSubmit}>
                        {/* Loading Overlay */}
                        {isSubmittingForm && (
                            <div className="form-loading-overlay">
                                <div className="loading-content">
                                    <div className="loading-spinner"></div>
                                    <p>Submitting FSR...</p>
                                </div>
                            </div>
                        )}
                        
                        {/* Error Message */}
                        {submissionError && (
                            <div className="form-error-message">
                                <p>{submissionError}</p>
                            </div>
                        )}
                        
                        {/* Internal Notes Section - Moved to top */}
                        <div className="form-group internal-notes-section">
                            <div className="internal-notes-header">
                                <label htmlFor="notes">Internal Notes</label>
                                <button 
                                    type="button" 
                                    className="btn-toggle-notes"
                                    onClick={handleToggleInternalNotes}
                                >
                                    {showInternalNotes ? 'Hide' : 'Show'}
                                </button>
                            </div>
                            {showInternalNotes && (
                                <textarea
                                    id="notes"
                                    value={completionData.notes}
                                    onChange={(e) => handleInputChange('notes', e.target.value)}
                                    placeholder="Enter internal notes..."
                                    rows="15"
                                />
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="actualStartTime">Actual Start Time</label>
                            <input
                                type="datetime-local"
                                id="actualStartTime"
                                value={completionData.actualStartTime}
                                onChange={(e) => handleInputChange('actualStartTime', e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="actualEndTime">Actual End Time</label>
                            <input
                                type="datetime-local"
                                id="actualEndTime"
                                value={completionData.actualEndTime}
                                onChange={(e) => handleInputChange('actualEndTime', e.target.value)}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Travel Duration</label>
                            <div className="duration-inputs">
                                <div className="duration-field">
                                    <input
                                        type="number"
                                        id="travelHours"
                                        value={completionData.travelHours}
                                        onChange={(e) => handleInputChange('travelHours', e.target.value)}
                                        placeholder="0"
                                        min="0"
                                        required
                                    />
                                    <label htmlFor="travelHours">Hours</label>
                                </div>
                                <div className="duration-field">
                                    <input
                                        type="number"
                                        id="travelMinutes"
                                        value={completionData.travelMinutes}
                                        onChange={(e) => handleInputChange('travelMinutes', e.target.value)}
                                        placeholder="0"
                                        min="0"
                                        max="59"
                                        required
                                    />
                                    <label htmlFor="travelMinutes">Minutes</label>
                                </div>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>On-Site Duration</label>
                            <div className="duration-inputs">
                                <div className="duration-field">
                                    <input
                                        type="number"
                                        id="onsiteHours"
                                        value={completionData.onsiteHours}
                                        onChange={(e) => handleInputChange('onsiteHours', e.target.value)}
                                        placeholder="0"
                                        min="0"
                                        required
                                    />
                                    <label htmlFor="onsiteHours">Hours</label>
                                </div>
                                <div className="duration-field">
                                    <input
                                        type="number"
                                        id="onsiteMinutes"
                                        value={completionData.onsiteMinutes}
                                        onChange={(e) => handleInputChange('onsiteMinutes', e.target.value)}
                                        placeholder="0"
                                        min="0"
                                        max="59"
                                        required
                                    />
                                    <label htmlFor="onsiteMinutes">Minutes</label>
                                </div>
                            </div>
                        </div>

                        {!isInstAppointment && (
                            <div className="form-group">
                                <label htmlFor="troubleDescription">Description of Trouble</label>
                                <textarea
                                    id="troubleDescription"
                                    value={completionData.troubleDescription}
                                    onChange={(e) => handleInputChange('troubleDescription', e.target.value)}
                                    placeholder="Enter description of trouble..."
                                    rows="15"
                                    maxLength={1500}
                                    required
                                />
                            </div>
                        )}

                        {!isInstAppointment && (
                            <div className="form-group">
                                <label htmlFor="repairsDescription">Description of Repairs</label>
                                <textarea
                                    id="repairsDescription"
                                    value={completionData.repairsDescription}
                                    onChange={(e) => handleInputChange('repairsDescription', e.target.value)}
                                    placeholder="Enter description of repairs..."
                                    rows="15"
                                    maxLength={1500}
                                    required
                                />
                            </div>
                        )}

                        {isInstAppointment && (
                            <div className="form-group">
                                <div className="meeting-checkbox-list">
                                    <div className="meeting-checkbox-row">
                                        <label className="meeting-checkbox-label" htmlFor="safetyMeeting">Safety Meeting</label>
                                        <input
                                            className="meeting-checkbox-input"
                                            type="checkbox"
                                            id="safetyMeeting"
                                            checked={!!completionData.safetyMeeting}
                                            onChange={(e) => handleInputChange('safetyMeeting', e.target.checked)}
                                        />
                                    </div>
                                    <div className="meeting-checkbox-row">
                                        <label className="meeting-checkbox-label" htmlFor="oacMeeting">OAC meeting</label>
                                        <input
                                            className="meeting-checkbox-input"
                                            type="checkbox"
                                            id="oacMeeting"
                                            checked={!!completionData.oacMeeting}
                                            onChange={(e) => handleInputChange('oacMeeting', e.target.checked)}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label htmlFor="additionalComments">{isInstAppointment ? 'Daily Journal' : 'Additional Comments'}</label>
                            <textarea
                                id="additionalComments"
                                value={completionData.additionalComments}
                                onChange={(e) => handleInputChange('additionalComments', e.target.value)}
                                placeholder={isInstAppointment ? 'Enter your Daily Journal...' : 'Enter additional comments...'}
                                rows="15"
                                maxLength={isInstAppointment ? 3600 : 1200}
                                required={isInstAppointment}
                            />
                        </div>

                        <div className="form-group associated-help-section">
                            <label htmlFor="associatedHelpInput">Associated Help</label>

                            {(completionData.associatedHelp || []).length > 0 && (
                                <div className="associated-help-selected-list">
                                    {(completionData.associatedHelp || []).map((helper, index) => (
                                        <div className="associated-help-selected-item" key={`${helper.type || ''}-${helper.id || helper.email || helper.name}-${index}`}>
                                            <div className="associated-help-selected-text">
                                                {getAssociatedHelperLabel(helper)}
                                            </div>
                                            <button
                                                type="button"
                                                className="associated-help-remove-btn"
                                                onClick={() => handleRemoveAssociatedHelper(helper)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <input
                                id="associatedHelpInput"
                                type="text"
                                value={associatedHelpInput}
                                onChange={(e) => {
                                    setAssociatedHelpInput(e.target.value);
                                    if (!showCreateExternalHelp) {
                                        setShowAssociatedHelpDropdown(true);
                                    }
                                }}
                                placeholder="Search for associated help..."
                            />

                            {showAssociatedHelpDropdown && associatedHelpInput.trim() !== '' && !showCreateExternalHelp && (
                                <div className="associated-help-dropdown">
                                    {filteredAssociatedHelpers.internalTechs.length > 0 && (
                                        <div className="associated-help-group">
                                            <div className="associated-help-group-title">Internal</div>
                                            {filteredAssociatedHelpers.internalTechs.map((helper) => (
                                                <button
                                                    type="button"
                                                    key={`internal-${helper.id}`}
                                                    className="associated-help-option"
                                                    onClick={() => handleSelectAssociatedHelper({
                                                        id: helper.id,
                                                        name: helper.name,
                                                        email: helper.email,
                                                        branch: helper.branch,
                                                        type: 'internal'
                                                    })}
                                                >
                                                    {getAssociatedHelperLabel(helper)}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {filteredAssociatedHelpers.externalTechs.length > 0 && (
                                        <div className="associated-help-group">
                                            <div className="associated-help-group-title">External</div>
                                            {filteredAssociatedHelpers.externalTechs.map((helper) => (
                                                <button
                                                    type="button"
                                                    key={`external-${helper.id}`}
                                                    className="associated-help-option"
                                                    onClick={() => handleSelectAssociatedHelper({
                                                        id: helper.id,
                                                        company: helper.company,
                                                        name: helper.name,
                                                        phone: helper.phone,
                                                        email: helper.email,
                                                        createdBy: helper.createdBy,
                                                        createdDate: helper.createdDate,
                                                        branch: helper.branch,
                                                        active: helper.active,
                                                        type: 'external'
                                                    })}
                                                >
                                                    {getAssociatedHelperLabel(helper)}
                                                </button>
                                            ))}
                                        </div>
                                    )}

                                    {filteredAssociatedHelpers.internalTechs.length < 1 && filteredAssociatedHelpers.externalTechs.length < 1 && (
                                        <button
                                            type="button"
                                            className="associated-help-create-option"
                                            onClick={handleStartCreateExternalHelp}
                                        >
                                            <span className="associated-help-create-plus">+</span>
                                            {` Create Record for "${associatedHelpInput.trim()}"`}
                                        </button>
                                    )}
                                </div>
                            )}

                            {showCreateExternalHelp && (
                                <div className="associated-help-create-form">
                                    <input
                                        type="text"
                                        value={newExternalHelp.company}
                                        onChange={(e) => setNewExternalHelp(prev => ({ ...prev, company: e.target.value }))}
                                        placeholder="Company"
                                    />
                                    <input
                                        type="text"
                                        value={newExternalHelp.name}
                                        onChange={(e) => setNewExternalHelp(prev => ({ ...prev, name: e.target.value }))}
                                        placeholder="Name (required)"
                                        required
                                    />
                                    <input
                                        type="text"
                                        value={newExternalHelp.phone}
                                        onChange={(e) => setNewExternalHelp(prev => ({ ...prev, phone: e.target.value }))}
                                        placeholder="Phone"
                                    />
                                    <input
                                        type="email"
                                        value={newExternalHelp.email}
                                        onChange={(e) => setNewExternalHelp(prev => ({ ...prev, email: e.target.value }))}
                                        placeholder="Email"
                                    />
                                    {getExternalHelpMissingRequirements().length > 0 && (
                                        <div className="associated-help-warning">
                                            {getExternalHelpMissingRequirements().join(' ')}
                                        </div>
                                    )}
                                    <div className="associated-help-create-actions">
                                        <button
                                            type="button"
                                            className="btn-cancel"
                                            onClick={() => {
                                                setShowCreateExternalHelp(false);
                                                setNewExternalHelp({ company: '', name: '', phone: '', email: '' });
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            className={`btn-submit ${!canCreateExternalHelp() ? 'btn-disabled' : ''}`}
                                            disabled={!canCreateExternalHelp()}
                                            onClick={handleAddExternalHelp}
                                        >
                                            Add Help
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="form-group attachments-section">
                            <label htmlFor="appointmentAttachments">Attachments</label>

                            {(completionData.attachments || []).length > 0 && (
                                <div className="associated-help-selected-list">
                                    {(completionData.attachments || []).map((attachment, index) => (
                                        <div className="associated-help-selected-item" key={`${attachment.fileName}-${attachment.size}-${attachment.lastModified}-${index}`}>
                                            <div className="associated-help-selected-text">
                                                {attachment.fileName}
                                            </div>
                                            <button
                                                type="button"
                                                className="associated-help-remove-btn"
                                                onClick={() => handleRemoveAttachment(attachment)}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            <input
                                id="appointmentAttachments"
                                type="file"
                                multiple
                                onChange={handleAttachmentFilesSelected}
                            />
                        </div>

                        {/* Technician Sign FSR Button - First */}
                        <button 
                            type="button" 
                            className={`btn-sign-fsr ${completionData.techSignature ? 'signature-captured' : ''}`}
                            onClick={handleOpenTechSignatureModal}
                            disabled={!!completionData.techSignature}
                        >
                            {completionData.techSignature ? 'Technician Signature Captured' : 'Technician - Sign FSR'}
                        </button>

                        {/* Customer Sign FSR Button - Second */}
                        <button 
                            type="button" 
                            className={`btn-sign-fsr ${completionData.signature ? 'signature-captured' : ''}`}
                            onClick={handleOpenSignatureModal}
                            disabled={!!completionData.signature}
                        >
                            {completionData.signature ? 'Customer Signature Captured' : 'Customer - Sign FSR'}
                        </button>

                        {/* Requirements Status Section */}
                        <div className="requirements-status">
                            <h3>Required for Submission:</h3>
                            <div className="requirements-list">
                                <div className={`requirement-item ${
                                    (completionData.travelHours && completionData.travelHours !== '') || 
                                    (completionData.travelMinutes && completionData.travelMinutes !== '') 
                                    ? 'completed' : 'pending'
                                }`}>
                                    <span className="requirement-icon">
                                        {(completionData.travelHours && completionData.travelHours !== '') || 
                                         (completionData.travelMinutes && completionData.travelMinutes !== '') 
                                         ? '✓' : '○'}
                                    </span>
                                    <span className="requirement-text">Travel Duration</span>
                                </div>
                                
                                <div className={`requirement-item ${
                                    (completionData.onsiteHours && completionData.onsiteHours !== '') || 
                                    (completionData.onsiteMinutes && completionData.onsiteMinutes !== '') 
                                    ? 'completed' : 'pending'
                                }`}>
                                    <span className="requirement-icon">
                                        {(completionData.onsiteHours && completionData.onsiteHours !== '') || 
                                         (completionData.onsiteMinutes && completionData.onsiteMinutes !== '') 
                                         ? '✓' : '○'}
                                    </span>
                                    <span className="requirement-text">On-Site Duration</span>
                                </div>
                                
                                {!isInstAppointment && (
                                    <div className={`requirement-item ${
                                        completionData.troubleDescription && completionData.troubleDescription.trim() !== '' 
                                        ? 'completed' : 'pending'
                                    }`}>
                                        <span className="requirement-icon">
                                            {completionData.troubleDescription && completionData.troubleDescription.trim() !== '' 
                                             ? '✓' : '○'}
                                        </span>
                                        <span className="requirement-text">Description of Trouble</span>
                                    </div>
                                )}
                                
                                {!isInstAppointment && (
                                    <div className={`requirement-item ${
                                        completionData.repairsDescription && completionData.repairsDescription.trim() !== '' 
                                        ? 'completed' : 'pending'
                                    }`}>
                                        <span className="requirement-icon">
                                            {completionData.repairsDescription && completionData.repairsDescription.trim() !== '' 
                                             ? '✓' : '○'}
                                        </span>
                                        <span className="requirement-text">Description of Repairs</span>
                                    </div>
                                )}
                                
                                {!isInstAppointment && (
                                    <div className={`requirement-item ${
                                        completionData.signature && completionData.signature !== '' 
                                        ? 'completed' : 'pending'
                                    }`}>
                                        <span className="requirement-icon">
                                            {completionData.signature && completionData.signature !== '' 
                                             ? '✓' : '○'}
                                        </span>
                                        <span className="requirement-text">Customer Signature</span>
                                    </div>
                                )}

                                {isInstAppointment && (
                                    <div className={`requirement-item ${
                                        completionData.additionalComments && completionData.additionalComments.trim() !== ''
                                        ? 'completed' : 'pending'
                                    }`}>
                                        <span className="requirement-icon">
                                            {completionData.additionalComments && completionData.additionalComments.trim() !== ''
                                             ? '✓' : '○'}
                                        </span>
                                        <span className="requirement-text">Daily Journal</span>
                                    </div>
                                )}

                                <div className={`requirement-item ${
                                    completionData.techSignature && completionData.techSignature !== '' 
                                    ? 'completed' : 'pending'
                                }`}>
                                    <span className="requirement-icon">
                                        {completionData.techSignature && completionData.techSignature !== '' 
                                         ? '✓' : '○'}
                                    </span>
                                    <span className="requirement-text">Technician Signature</span>
                                </div>
                            </div>
                            
                            {isFormReadyForSubmission() ? (
                                <div className="requirements-complete">
                                    <span className="complete-icon">✅</span>
                                    <span className="complete-text">All requirements completed - Ready to submit!</span>
                                </div>
                            ) : (
                                <div className="requirements-incomplete">
                                    <span className="incomplete-text">Please complete the items above to enable submission.</span>
                                </div>
                            )}
                        </div>

                        <div className="form-actions">
                            <button type="button" className="btn-cancel" onClick={() => setShowCompletionForm(false)}>
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className={`btn-submit ${(!isFormReadyForSubmission() || isSubmittingForm) ? 'btn-disabled' : ''}`}
                                disabled={!isFormReadyForSubmission() || isSubmittingForm}
                            >
                                {isSubmittingForm ? 'Submitting...' : 'Complete Appointment'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="TechApp">
            <div className="appointment-header">
                <h1>{appointmentRecord.appointmentID}</h1>
                <div className="header-buttons">
                    {statusActionLabel && (
                        <button className="btn-status-update" onClick={handleUpdateStatus} disabled={isUpdatingStatus}>
                            {isUpdatingStatus ? 'Updating...' : statusActionLabel}
                        </button>
                    )}
                    <button className="btn-complete" onClick={handleCompleteAppointment}>
                        Complete Appointment
                    </button>
                    <button className="btn-manage-equipment" onClick={handleManageEquipment}>
                        Manage Equipment
                    </button>   
                </div>
            </div>

            <div className="appointment-details">
                <div className="detail-section">
                    <h2>Appointment Information</h2>
                    <div className="detail-row">
                        <span className="label">Appointment ID:</span>
                        <span className="value">{appointmentRecord.appointmentID}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Type:</span>
                        <span className="value">{appointmentRecord.type === 'INST' ? 'Daily Log' : appointmentRecord.type}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Scheduled Start:</span>
                        <span className="value">{formatDateTime(appointmentRecord.scheduledStart)}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Scheduled End:</span>
                        <span className="value">{formatDateTime(appointmentRecord.scheduledEnd)}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Description:</span>
                        <span className="value">{appointmentRecord.description}</span>
                    </div>
                    {appointmentRecord.officeNotes && (
                        <div className="detail-row">
                            <span className="label">Office Notes:</span>
                            <div className="notes-content">
                                {appointmentRecord.officeNotes}
                            </div>
                        </div>
                    )}
                    {appointmentRecord.linkedAppointmentNumber && (
                        <div className="detail-row">
                            <span className="label">Follow-up Appointment For:</span>
                            <span className="value">{appointmentRecord.linkedAppointmentNumber}</span>
                        </div>
                    )}
                </div>

                <div className="detail-section">
                    <h2>Customer & Location</h2>
                    <div className="detail-row">
                        <span className="label">Customer:</span>
                        <span className="value">{appointmentRecord.customer?.customerName}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Customer Number:</span>
                        <span className="value">{appointmentRecord.customer?.customerNumber}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Location:</span>
                        <span className="value">{appointmentRecord.location?.locationName}</span>
                    </div>
                    <div className="detail-row">
                        <span className="label">Address:</span>
                        <span className="value">
                            {appointmentRecord.location?.address?.street}
                            {appointmentRecord.location?.address?.street2 && <br />}
                            {appointmentRecord.location?.address?.street2}
                            <br />
                            {appointmentRecord.location?.address?.city}, {appointmentRecord.location?.address?.state} {appointmentRecord.location?.address?.zip}
                        </span>
                    </div>
                    {appointmentRecord.location?.address && (
                        <div className="detail-row">
                            <button 
                                className="btn-navigate"
                                onClick={() => {
                                    const address = appointmentRecord.location.address;
                                    const fullAddress = `${address.street}${address.street2 ? ' ' + address.street2 : ''}, ${address.city}, ${address.state} ${address.zip}`;
                                    const encodedAddress = encodeURIComponent(fullAddress);
                                    window.open(`https://maps.google.com/maps?daddr=${encodedAddress}`, '_blank');
                                }}
                            >
                                Navigate to Appointment
                            </button>
                        </div>
                    )}
                </div>

                <div className="detail-section">
                    <h2>Contact Information</h2>
                    <div className="detail-row">
                        <span className="label">Contact:</span>
                        <span className="value">{appointmentRecord.contact?.name}</span>
                    </div>
                    {appointmentRecord.contact?.title && (
                        <div className="detail-row">
                            <span className="label">Title:</span>
                            <span className="value">{appointmentRecord.contact?.title}</span>
                        </div>
                    )}
                    {appointmentRecord.contact?.number && (
                        <div className="detail-row">
                            <span className="label">Phone:</span>
                            <span className="value">
                                <a href={`tel:${appointmentRecord.contact?.number}`}>
                                    {appointmentRecord.contact?.number}
                                </a>
                            </span>
                        </div>
                    )}
                    <div className="detail-row">
                        <span className="label">Email:</span>
                        <span className="value">
                            <a href={`mailto:${appointmentRecord.contact?.email}`}>
                                {appointmentRecord.contact?.email}
                            </a>
                        </span>
                    </div>
                </div>

                {(appointmentRecord.fileShareLink || appointmentRecord.pmChecklist) && (
                    <div className="detail-section">
                        <h2>File Share</h2>
                        {appointmentRecord.fileShareLink && (
                            <div className="detail-row">
                                <span className="label">Folder Link:</span>
                                <span className="value">
                                    <a href={appointmentRecord.fileShareLink} target="_blank" rel="noopener noreferrer">
                                        Access Files
                                    </a>
                                </span>
                            </div>
                        )}
                        {appointmentRecord.pmChecklist && (
                            <div className="detail-row">
                                <span className="label">PM Checklist Link:</span>
                                <span className="value">
                                    <a href={appointmentRecord.pmChecklist} target="_blank" rel="noopener noreferrer">
                                        View PM Checklist
                                    </a>
                                </span>
                            </div>
                        )}
                    </div>
                )}

            </div>

            {previousWork.length > 0 && (
                <div className="previous-work-section">
                    <h2>Previous Work</h2>
                    <div className="previous-work-list">
                        {previousWork.map((work, index) => (
                        
                            <div key={work.appointmentID || index} className="previous-work-item">
                                <div 
                                    className="previous-work-header" 
                                    onClick={() => togglePreviousWorkExpanded(work.appointmentID || index)}
                                >
                                    <div className="previous-work-summary">
                                        <span className="appointment-number">
                                            {work.appointmentID || `Appointment ${index + 1}`}
                                        </span>
                                        {work.description && (
                                            <span className="appointment-description">
                                                {work.description}
                                            </span>
                                        )}
                                        <span className="appointment-date">
                                            {formatAppointmentDate(work.scheduledStart || work.scheduledEnd)}
                                        </span>
                                    </div>
                                    <span className="expand-icon">
                                        {expandedPreviousWork[work.appointmentID || index] ? '▲' : '▼'}
                                    </span>
                                </div>
                                
                                {expandedPreviousWork[work.appointmentID || index] && (
                                    <div className="previous-work-details">
                                        {work.type && (
                                            <div className="detail-row">
                                                <span className="label">Type:</span>
                                                <span className="value">{work.type === 'INST' ? 'Daily Log' : work.type}</span>
                                            </div>
                                        )}
                                        
                                        {work.status && (
                                            <div className="detail-row">
                                                <span className="label">Status:</span>
                                                <span className="value">{work.status}</span>
                                            </div>
                                        )}
                                        
                                        {work.description && (
                                            <div className="detail-row">
                                                <span className="label">Description:</span>
                                                <span className="value">{work.description}</span>
                                            </div>
                                        )}
                                        
                                        {work.scheduledStart && (
                                            <div className="detail-row">
                                                <span className="label">Scheduled Start:</span>
                                                <span className="value">{formatDateTime(work.scheduledStart)}</span>
                                            </div>
                                        )}
                                        
                                        {work.scheduledEnd && (
                                            <div className="detail-row">
                                                <span className="label">Scheduled End:</span>
                                                <span className="value">{formatDateTime(work.scheduledEnd)}</span>
                                            </div>
                                        )}
                                        
                                        {work.troubleDescription && (
                                            <div className="detail-row">
                                                <span className="label">Trouble Description:</span>
                                                <span className="value">{work.troubleDescription}</span>
                                            </div>
                                        )}
                                        
                                        {work.repairDescription && (
                                            <div className="detail-row">
                                                <span className="label">Repair Description:</span>
                                                <span className="value">{work.repairDescription}</span>
                                            </div>
                                        )}
                                        
                                        {(work.travelHours || work.travelMinutes) && (
                                            <div className="detail-row">
                                                <span className="label">Travel Time:</span>
                                                <span className="value">
                                                    {work.travelHours || 0} hours, {work.travelMinutes || 0} minutes
                                                </span>
                                            </div>
                                        )}
                                        
                                        {(work.onsiteHours || work.onsiteMinutes) && (
                                            <div className="detail-row">
                                                <span className="label">On-Site Time:</span>
                                                <span className="value">
                                                    {work.onsiteHours || 0} hours, {work.onsiteMinutes || 0} minutes
                                                </span>
                                            </div>
                                        )}
                                        
                                        {work.officeNotes && (
                                            <div className="detail-row">
                                                <span className="label">Office Notes:</span>
                                                <span className="value">{work.officeNotes}</span>
                                            </div>
                                        )}
                                        
                                        {work.techNotes && (
                                            <div className="detail-row">
                                                <span className="label">Internal Notes:</span>
                                                <span className="value">{work.techNotes}</span>
                                            </div>
                                        )}
                                        
                                        {work.additionalComments && work.additionalComments !== "No Comments Provided" && (
                                            <div className="detail-row">
                                                <span className="label">Additional Comments:</span>
                                                <span className="value">{work.additionalComments}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TechApp;